import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Monitor, Copy, Download, Edit, Loader2 } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useUserCheck } from '../hooks/api/useUserCheck'
import { useDevice, useUpdateDevice } from '../hooks/api/useDevices'
import { useDevicesLive } from '../hooks/api/useDevicesLive'
import { DashboardLayout } from '../components/layout/DashboardLayout'
import { EditDeviceModal } from '../components/modals/EditDeviceModal'
import { usePermissions } from '../hooks/usePermissions'
import { Select } from '../components/ui/Select'
import { supabase } from '../lib/supabase'

// Helper function to format device data for display
const formatDeviceForDisplay = (device: any) => ({
  id: device.id,
  displayName: device.name,
  deviceType: device.device_type_display || device.os_type || 'Unknown',
  status: device.status,
  loggedInUser: device.status === 'online' && device.logged_in_user ? device.logged_in_user : '',
  localIp: device.ip_address || '',
  organization: device.organization_name || 'WCD',
  lastActive: device.last_seen ? new Date(device.last_seen).toLocaleString() : '',
  hasAgent: !!device.agent_version,
  // Use real data from database
  operatingSystem: device.operating_system || 'Unknown OS',
  uptime: device.uptime || '',
  lastSeen: device.last_seen_display || '',
  notifications: device.notifications_count || 0,
  cpuUsage: device.cpu_usage || 0,
  memoryUsage: device.memory_usage || 0,
  storageUsage: device.storage_usage || 0,
  hasFullData: device.has_full_data || false
})

export const DeviceDetailPage = () => {
  const { deviceId } = useParams<{ deviceId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: userRecord, isLoading, error } = useUserCheck(user?.id || '')
  const { data: device, isLoading: deviceLoading } = useDevice(deviceId || '')
  const { data: kaseyaDevices = [], isLoading: kaseyaLoading } = useDevicesLive()
  const { hasPermission } = usePermissions({ userRole: userRecord?.role || null })
  const updateDeviceMutation = useUpdateDevice()
  const [selectedArchitecture, setSelectedArchitecture] = useState('64-bit')
  const [isSendingAgent, setIsSendingAgent] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [assetData, setAssetData] = useState<any>(null)
  const [assetLoading, setAssetLoading] = useState(false)

  // Try to find device in local DB first, then in Kaseya cache
  const kaseyaDevice = kaseyaDevices.find(d => d.kaseya_device_id === deviceId || d.id === deviceId)

  // Fetch asset data on-demand for Kaseya devices
  useEffect(() => {
    const fetchAssetData = async () => {
      // Only fetch for Kaseya devices (not in local DB)
      if (!device && kaseyaDevice && deviceId) {
        setAssetLoading(true)
        try {
          console.log('Fetching asset data for device:', deviceId)
          const { data, error } = await supabase.functions.invoke('kaseya-fetch-device-assets', {
            body: { device_id: deviceId }
          })

          if (error) {
            console.error('Error fetching asset data:', error)
          } else if (data?.success) {
            console.log('Asset data fetched:', data)
            setAssetData(data.data)
          }
        } catch (err) {
          console.error('Failed to fetch asset data:', err)
        } finally {
          setAssetLoading(false)
        }
      }
    }

    fetchAssetData()
  }, [device, kaseyaDevice, deviceId])

  // Format device data - prefer local DB, fall back to Kaseya cache with asset data
  const deviceData = device
    ? formatDeviceForDisplay(device)
    : kaseyaDevice
    ? formatDeviceForDisplay({
        id: kaseyaDevice.kaseya_device_id,
        name: kaseyaDevice.name,
        status: assetData?.status || kaseyaDevice.status,
        os_type: kaseyaDevice.group_name,
        organization_name: kaseyaDevice.company_name || kaseyaDevice.organization_name,
        ip_address: assetData?.ip_address || kaseyaDevice.ip_address || '',
        logged_in_user: assetData?.logged_in_user || kaseyaDevice.logged_in_user || '',
        last_seen: kaseyaDevice.last_synced_at,
        // Use fetched asset data
        operating_system: assetData?.operating_system || kaseyaDevice.operating_system || '',
        uptime: assetData?.uptime || kaseyaDevice.uptime || '',
        cpu_usage: assetData?.cpu_usage_percent || kaseyaDevice.cpu_usage_percent || 0,
        memory_usage: assetData?.memory_usage_percent || kaseyaDevice.memory_usage_percent || 0,
        storage_usage: assetData?.disk_usage_percent || kaseyaDevice.disk_usage_percent || 0,
        notifications_count: assetData?.notifications_count || 0,
        has_full_data: assetData?.has_asset_data || kaseyaDevice.has_asset_data || false,
        is_kaseya_device: true
      })
    : null

  // Debug: Log device data when it changes
  useEffect(() => {
    if (device) {
      console.log('DeviceDetailPage: Device data updated:', device)
      console.log('DeviceDetailPage: Formatted device data:', deviceData)
    }
  }, [device, deviceData])

  const handleSendAgent = async () => {
    setIsSendingAgent(true)
    // TODO: Implement actual agent sending API call
    console.log('Sending agent installer for architecture:', selectedArchitecture)
    await new Promise(resolve => setTimeout(resolve, 2000))
    setIsSendingAgent(false)
  }

  const handleCopyDownloadLink = () => {
    // TODO: Generate actual download link
    const downloadLink = `https://helpdesk411.com/agent/installer/${deviceId}/${selectedArchitecture}`
    navigator.clipboard.writeText(downloadLink)
    // TODO: Show success toast
    console.log('Download link copied:', downloadLink)
  }

  const handleEditDevice = () => {
    setShowEditModal(true)
  }

  const handleSaveDeviceDetails = async (details: any) => {
    if (!deviceId) return
    
    try {
      console.log('DeviceDetailPage: Saving device details:', details)
      const updatedDevice = await updateDeviceMutation.mutateAsync({
        deviceId,
        updates: details
      })
      console.log('DeviceDetailPage: Device updated successfully:', updatedDevice)
    } catch (error) {
      console.error('Error updating device details:', error)
      throw error
    }
  }

  if (isLoading || deviceLoading || kaseyaLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-400 border-t-transparent border-r-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading device details...</p>
        </div>
      </div>
    )
  }

  if (!deviceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600 text-sm font-medium mb-2">❌ Device not found!</p>
            <p className="text-red-500 text-xs">The device you're looking for doesn't exist or you don't have permission to view it.</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !userRecord) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600 text-sm font-medium mb-2">❌ User not found in database!</p>
            <p className="text-red-500 text-xs mb-2">This account was created before user record creation was implemented.</p>
            <p className="text-red-500 text-xs">Please create a new account to test the full flow.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DashboardLayout 
      userRole={userRecord.role} 
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-6">
        {/* Header with Back Button */}
        <div className="space-y-4">
          <div className='flex items-center gap-4'>
            <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-2xl font-semibold text-gray-900">Overview</h1>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-gray-400 text-lg">{deviceData.displayName}</p>
            {hasPermission('EDIT_DEVICES') && (
              <button
                onClick={handleEditDevice}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Device
              </button>
            )}
          </div>
          <div>
            <div className="flex items-center gap-3">
              {/* Status Badge */}
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                deviceData.status === 'online' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-700'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  deviceData.status === 'online' ? 'bg-green-500' : 'bg-gray-400'
                }`}></div>
                <span className="text-sm font-medium capitalize">{deviceData.status}</span>
              </div>
              
              {/* Device Type Badge */}
              <div className="px-2 py-0.5 bg-white border border-gray-200 rounded-full">
                <span className="text-xs font-medium text-gray-700 capitalize">{deviceData.deviceType}</span>
              </div>
              
              {/* Notifications */}
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-xs font-medium text-gray-700">{deviceData.notifications} Notifications</span>
              </div>
            </div>
          </div>
        </div>

        {/* Logged-In Users Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-[0_2px_8px_0_rgba(0,0,0,0.1)] p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Logged-In Users</h3>
            <div className="space-y-3">
              {deviceData.loggedInUser ? (
                <div className="flex items-center space-x-3 p-3 bg-gray-100 rounded-lg">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {deviceData.loggedInUser.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{deviceData.loggedInUser}</p>
                    <p className="text-xs text-gray-500">Active now</p>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center">
                  <div className="border-t border-dashed border-gray-300"></div>
                  <p className="text-sm text-gray-500 mt-4">
                    {deviceData.status === 'offline' ? 'No users logged in (device offline)' : 'No users currently logged in'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Device Information and Resource Usage Cards - Only show if device has full data */}
          {assetLoading ? (
            /* Loading state while fetching asset data */
            <div className="bg-white rounded-xl border border-gray-200 shadow-[0_2px_8px_0_rgba(0,0,0,0.1)] p-10">
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Device Data</h3>
                <p className="text-gray-400 text-sm">Fetching resource usage from Kaseya...</p>
              </div>
            </div>
          ) : deviceData.hasFullData ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Device Information Card */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-[0_2px_8px_0_rgba(0,0,0,0.1)] p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-600">Operating System:</span>
                    <span className="text-sm font-medium text-gray-900">{deviceData.operatingSystem}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-600">IP Address:</span>
                    <span className="text-sm font-medium text-gray-900">{deviceData.localIp}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-600">Uptime:</span>
                    <span className="text-sm font-medium text-gray-900">{deviceData.uptime}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-600">Last Seen:</span>
                    <span className="text-sm font-medium text-gray-900">{deviceData.lastSeen}</span>
                  </div>
                </div>
              </div>

              {/* Resource Usage Card */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-[0_2px_8px_0_rgba(0,0,0,0.1)] p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Resource Usage</h3>
                <div className="space-y-4">
                  {/* CPU Usage */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">CPU</span>
                      <span className="text-sm font-medium text-gray-900">{deviceData.cpuUsage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${deviceData.cpuUsage}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Memory Usage */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Memory</span>
                      <span className="text-sm font-medium text-gray-900">{deviceData.memoryUsage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${deviceData.memoryUsage}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Storage Usage */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Storage</span>
                      <span className="text-sm font-medium text-gray-900">{deviceData.storageUsage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${deviceData.storageUsage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* No Device Data Available Card - Show for devices without full data */
            <div className="bg-white rounded-xl border border-gray-200 shadow-[0_2px_8px_0_rgba(0,0,0,0.1)] p-10">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Monitor className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Device Data Available</h3>
                <p className="text-gray-400 text-sm leading-relaxed max-w-md mx-auto">
                  Install the agent on this device to view device information, resource usage, and logged-in users.
                </p>
              </div>
            </div>
          )}

        {/* Send Agent Installer Card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-[0_2px_8px_0_rgba(0,0,0,0.1)] p-10">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Send Agent Installer</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-600 mb-2">
                Select Architecture
              </label>
              <Select
                value={selectedArchitecture}
                onChange={(e) => setSelectedArchitecture(e.target.value)}
                className="w-full max-w-xs"
              >
                <option value="32-bit">32-bit</option>
                <option value="64-bit">64-bit</option>
                <option value="ARM64">ARM64</option>
              </Select>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleSendAgent}
                disabled={isSendingAgent}
                className="inline-flex items-center px-6 py-4 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                {isSendingAgent ? 'Sending...' : `Send Agent (${selectedArchitecture})`}
              </button>
              
              <button
                onClick={handleCopyDownloadLink}
                className="inline-flex items-center px-6 py-4 bg-white text-gray-700 border border-gray-300 text-sm font-medium rounded-full hover:bg-gray-50 transition-colors"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Download Link
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Device Modal */}
      <EditDeviceModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveDeviceDetails}
        deviceId={deviceId || ''}
        currentDetails={(() => {
          if (!device) return undefined;
          
                const details = {
                  status: device.status || 'offline',
                  operating_system: device.operating_system || device.os_type || '',
                  ip_address: device.ip_address || '',
                  uptime: device.uptime || '',
                  last_seen_display: device.last_seen_display || '',
                  notifications_count: device.notifications_count || 0,
                  cpu_usage: device.cpu_usage || 0,
                  memory_usage: device.memory_usage || 0,
                  storage_usage: device.storage_usage || 0,
                  logged_in_user: device.logged_in_user || '',
                  has_full_data: device.has_full_data || false,
                  device_id_display: device.device_id_display || `DEV-${device.id.slice(-3).toUpperCase()}`,
                  user_email: device.user_email || '',
                  user_full_name: device.user_full_name || device.name || '',
                  ticket_count: device.ticket_count || 0,
                  last_active: device.last_active || '',
                  device_type_display: device.device_type_display || device.metadata?.device_type || device.os_type || '',
                  organization_name: device.organization_name || 'WCD'
                };
          
          console.log('DeviceDetailPage: Mapped currentDetails for modal:', details);
          return details;
        })()}
      />
    </DashboardLayout>
  )
}
